import { paginationTableProps, tableStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatPlaceOfInsurance } from "@/utils/formatUtils";
import { useFilterSearchParams } from "@/utils/hooksUtils";
import { URLQuery } from "@/utils/queryUtils";
import { numberOrZero } from "@/utils/utils";
import { Divider, Popconfirm, Table } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useDeleteCalcDraft, useFilterCalcDrafts } from "../../../../common/api/queries";
import ActionTextIcon from "../../../../common/components/icons/ActionTextIcon";
import AntIcon from "../../../../common/components/icons/AntIcon";
import PopconfirmDeleteIcon from "../../../../common/components/icons/PopconfirmDeleteIcon";
import Ellipsis from "../../../../common/components/views/Ellipsis";
import { QueryKeys, TableSizes } from "../../../../common/constants";
import { CalcType } from "../../enums";
import { CALC_ROUTE_PATHS } from "../../paths";
import { RealtyCalcDraftList } from "../types";
import { CalcDraftAttachmentsTableView } from "./CalcDraftAttachmentsTableView";

export const RealtyCalcDraftTableView = () => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const searchParam = useFilterSearchParams();

  const { data } = useFilterCalcDrafts({ ...searchParam, calcTypes: [CalcType.REALTY] });
  const deleteCalcDraft = useDeleteCalcDraft();

  const columns: ColumnsType<RealtyCalcDraftList> = [
    {
      key: "clientName",
      title: t("calc.draft.attrs.clientName"),
      width: 220,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{record.clientName}</Ellipsis>,
    },
    {
      key: "buildingType",
      title: t("calc.realty.enums.buildingType._label"),
      width: 130,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{t("calc.realty.enums.buildingType." + record.buildingType)}</Ellipsis>,
    },
    {
      key: "insurances",
      title: t("calc.draft.attrs.insurances"),
      children: [
        {
          key: "realty",
          title: t("calc.realty.sections.realtyInsurance"),
          align: "center",
          width: 130,
          render: (_, record) =>
            record.realtyInsuranceAmount ? (
              formatLocaleCurrency(record.realtyInsuranceAmount)
            ) : (
              <div className="center-align">
                <AntIcon type="close-circle" />
              </div>
            ),
        },
        {
          key: "household",
          title: t("calc.realty.sections.householdInsurance"),
          align: "center",
          width: 130,
          render: (_, record) =>
            record.householdInsuranceAmount ? (
              formatLocaleCurrency(record.householdInsuranceAmount)
            ) : (
              <div className="center-align">
                <AntIcon type="close-circle" />
              </div>
            ),
        },
      ],
    },
    {
      key: "placeOfInsurance",
      title: t("calc.draft.attrs.placeOfInsurance"),
      width: 240,
      ellipsis: { showTitle: false },
      render: (_, record) => <Ellipsis>{formatPlaceOfInsurance(record.placeOfInsurance)}</Ellipsis>,
    },
    {
      key: "attachmentsCount",
      title: t("calc.draft.attrs.attachmentsCount"),
      width: 60,
      align: "right",
      render: (_, record) => record.attachments?.length || 0,
    },
    {
      key: "actions",
      align: "right",
      fixed: "right",
      width: 190,
      render: (_, record) => (
        <>
          <ActionTextIcon
            path={`${CALC_ROUTE_PATHS.realty.to}${URLQuery.searchParamsToString({ searchParams: { [QueryKeys.DRAFT_ID]: record.id } })}`}
            target="_blank"
            icon="import"
            color="blue"
            text={t("calc.draft.actions.setToCalcForm")}
          />

          <Divider orientation="vertical" />

          <Popconfirm
            title={t("calc.draft.titles.deleteConfirm")}
            icon={<PopconfirmDeleteIcon />}
            okText={t("common.yes")}
            cancelText={t("common.no")}
            okType="danger"
            onConfirm={() => deleteCalcDraft.mutate(record.id)}
          >
            <ActionTextIcon icon="delete" color="red" text={t("common.delete")} />
          </Popconfirm>
        </>
      ),
    },
  ];

  return (
    <Table<RealtyCalcDraftList>
      {...tableStandardProps()}
      columns={columns}
      scroll={{ x: TableSizes.LARGE }}
      dataSource={data?.pageData as RealtyCalcDraftList[] | undefined}
      expandable={{
        expandedRowRender: (record) => <CalcDraftAttachmentsTableView calcDraft={record} />,
      }}
      pagination={{
        ...paginationTableProps,
        current: numberOrZero(data?.pageIndex) + 1,
        pageSize: data?.pageSize,
        total: data?.totalElementsCount,
        onChange: (pageIndex) => navigate({ search: URLQuery.getTableChangePageIndex(pageIndex) }, { replace: true }),
      }}
    />
  );
};
